/* GASSISA
=========================================================================================
* Descripción: Modal con el historial de nóminas aplicadas
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 07/08/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Agrega búsqueda manual de nóminas
*      3       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Agrega impresión de nómina desde historial
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, InputAdornment, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import TableViewIcon from "@mui/icons-material/TableView";
import EditIcon from "@mui/icons-material/Edit";
import EmptyIcon from "@mui/icons-material/Inbox";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";

import SizeSoftXModal from "../leyout/SizeSoftXModal";

const formatCurrency = (valor) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(valor) || 0);

const formatFecha = (fecha) => {
    const partes = String(fecha || "").split("-");
    if (partes.length !== 3) return fecha || "-";
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

export default function ModalHistorialNominas({ open, onClose, rows, cargando, onEditar, onVerDetalle, onImprimir }) {
    const verDetalle = onVerDetalle || (() => {});
    const editar = onEditar || (() => {});
    const imprimir = onImprimir || (() => {});
    const [busqueda, setBusqueda] = useState("");
    const filas = useMemo(() => {
        const keyword = busqueda.trim().toLowerCase();
        const lista = rows || [];
        if (!keyword) return lista;
        return lista.filter((fila) => {
            const periodo = `${formatFecha(fila.fechainicio)} ${formatFecha(fila.fechafin)}`.toLowerCase();
            return [fila.folio, fila.status, fila.total, periodo]
                .some((valor) => String(valor ?? "").toLowerCase().includes(keyword));
        });
    }, [busqueda, rows]);

    return (
        <SizeSoftXModal
            open={open}
            onClose={onClose}
            onSuccess={onClose}
            success="Cerrar"
            hideCancel
            maxWidth="lg"
            title="Buscar nóminas"
        >
            <Stack spacing={1.5}>
                <TextField autoComplete="off"
                    size="small"
                    label="Buscar nómina"
                    placeholder="Folio, periodo o estatus"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                {cargando ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : filas.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                        <EmptyIcon sx={{ fontSize: 42, mb: 1 }} />
                        <Typography>No se encontraron nóminas en el periodo consultado</Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: "52vh", overflow: "auto" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Folio</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Periodo</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filas.map((fila) => (
                                    <TableRow key={fila.id || fila.folio} hover>
                                        <TableCell>#{fila.folio}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {formatFecha(fila.fechainicio)} al {formatFecha(fila.fechafin)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={fila.status || "EN PROCESO"}
                                                size="small"
                                                color={fila.status === "TERMINADA" ? "success" : "default"}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {formatCurrency(fila.total)}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<TableViewIcon />}
                                                    onClick={() => verDetalle(fila)}
                                                >
                                                    Ver
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => editar(fila)}
                                                >
                                                    Editar
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<PrintIcon />}
                                                    onClick={() => imprimir(fila)}
                                                >
                                                    Imprimir
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Stack>
        </SizeSoftXModal>
    );
}